import { isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
  Directive,
  ElementRef,
  EventEmitter,
  NgZone,
  Output,
  PLATFORM_ID,
  Renderer2,
  inject,
} from '@angular/core';

/**
 * Emits whether the host's place in the document flow has scrolled entirely
 * above the viewport. Works on a sticky host, or a fixed one that leaves a
 * placeholder behind (`flSticky` does).
 *
 * A marker element is inserted before the host, so the host's parent must lay
 * its children out in normal flow: a flex or grid parent gives the marker a
 * gap or a track of its own, and `:first-child` / `nth-child` / `+` selectors
 * on the host stop matching.
 *
 * # Example:
 * <div flScrolledAboveViewport
 *   (scrolledAboveViewportChange)="handleScrolledAboveViewportChange($event)">
 * </div>
 */
@Directive({
  selector: `[flScrolledAboveViewport]`,
  standalone: true,
})
export class ScrolledAboveViewportDirective
  implements AfterViewInit, OnDestroy
{
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);
  private renderer = inject(Renderer2);

  /**
   * Fires with `true` once the host's box is completely above the viewport,
   * and with `false` once any part of it is back in view.
   */
  @Output() scrolledAboveViewportChange = new EventEmitter<boolean>();

  private marker = this.renderer.createElement('div') as HTMLDivElement;
  private observer?: IntersectionObserver;
  private resizeObserver?: ResizeObserver;
  private animationFrameRequestId: number | undefined = undefined;
  private observedHeight = 0;
  private scrolledAboveViewport = false;

  private get element(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.createMarker();

    const resizeObserver = new ResizeObserver(() => {
      if (this.animationFrameRequestId !== undefined) {
        cancelAnimationFrame(this.animationFrameRequestId);
      }
      this.animationFrameRequestId = requestAnimationFrame(() => {
        this.syncObserver(this.element.offsetHeight);
      });
    });
    resizeObserver.observe(this.element);
    this.resizeObserver = resizeObserver;
  }

  /**
   * A pinned host never leaves the viewport, so watch a marker left behind at
   * its place in the flow. The negative margin cancels out the height.
   */
  private createMarker(): void {
    this.renderer.setStyle(this.marker, 'height', '1px');
    this.renderer.setStyle(this.marker, 'margin-bottom', '-1px');
    this.renderer.setStyle(this.marker, 'pointer-events', 'none');
    this.renderer.setAttribute(this.marker, 'aria-hidden', 'true');
    this.renderer.insertBefore(
      this.element.parentNode,
      this.marker,
      this.element,
    );
  }

  /**
   * Growing the root by the host's height puts the crossing where the host
   * clears the viewport. `rootMargin` is fixed once an observer exists, so a
   * resize needs a new one.
   */
  private syncObserver(height: number): void {
    if (height === this.observedHeight) {
      return;
    }

    this.observedHeight = height;
    this.observer?.disconnect();
    this.observer = undefined;

    // A hidden or unrendered host has no place to scroll away from.
    if (height === 0) {
      if (this.scrolledAboveViewport) {
        this.scrolledAboveViewport = false;
        this.emit(false);
      }
      return;
    }

    this.observer = new IntersectionObserver(
      entries => entries.forEach(entry => this.handleEntry(entry)),
      { rootMargin: `${height}px 0px 0px 0px` },
    );
    this.observer.observe(this.marker);
  }

  private handleEntry(entry: IntersectionObserverEntry): void {
    // Only crossings are reported, so trust this one rather than re-deriving it
    // from the rect. `top` separates leaving through the top from the bottom.
    const scrolledAboveViewport =
      !entry.isIntersecting && entry.boundingClientRect.top < 0;

    if (scrolledAboveViewport !== this.scrolledAboveViewport) {
      this.scrolledAboveViewport = scrolledAboveViewport;
      this.emit(scrolledAboveViewport);
    }
  }

  /** zone.js does not patch ResizeObserver, so the observer runs outside it. */
  private emit(scrolledAboveViewport: boolean): void {
    this.ngZone.run(() =>
      this.scrolledAboveViewportChange.emit(scrolledAboveViewport),
    );
  }

  ngOnDestroy(): void {
    if (this.animationFrameRequestId !== undefined) {
      cancelAnimationFrame(this.animationFrameRequestId);
    }
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();

    if (this.marker.parentNode !== null) {
      this.renderer.removeChild(this.marker.parentNode, this.marker);
    }
    this.scrolledAboveViewportChange.complete();
  }
}
